import { Routes, Route, Navigate } from 'react-router-dom';
import { SiteChrome } from './components/SiteChrome';
import { HomePage } from './pages/HomePage';
import { SITE_ARCHE_PATH, type SiteBasePath } from './content';
import './site-theme.css';

/**
 * The public Fias marketing site, as an arche.
 *
 * Mounted twice:
 *  - at `/a/fias-site` (canonical), via the internal-arche registry
 *  - at `/` for signed-out visitors, via the root route in App.tsx
 *
 * `basePath` tells the shared chrome which of those it is rendering under so
 * internal links resolve to the right place. Signed-in users are redirected
 * off `/` into the app, but the site itself never checks auth — that decision
 * belongs to the route, and keeping it out of here is what allows the whole
 * surface to be prerendered to static HTML.
 */

interface FiasSiteArchePageProps {
  basePath?: SiteBasePath;
}

export function FiasSiteArchePage({ basePath = SITE_ARCHE_PATH }: FiasSiteArchePageProps) {
  // At the domain root only the front page is mounted — App.tsx routes "/"
  // exactly, so there is nothing for a nested router to match and declaring
  // one would warn about a parent route with no trailing "*".
  if (basePath === '/') {
    return (
      <SiteChrome basePath="/">
        <HomePage />
      </SiteChrome>
    );
  }

  return (
    <SiteChrome basePath={basePath}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* Unknown sub-paths fall back to the front page rather than a dead
            end — this is a marketing surface, not an app with real 404s. */}
        <Route path="*" element={<Navigate to={basePath} replace />} />
      </Routes>
    </SiteChrome>
  );
}

export default FiasSiteArchePage;
